import {
  Button,
  Card,
  Divider,
  Group,
  Icon,
  IconButton,
  Input,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
} from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationPreset,
  MotionAmplificationRawVideo,
} from "../../clients/motion-amplification";
import { useVideoFrameRate } from "../../hooks/useVideoFrameRate";
import { MultipartUploadPausedError } from "../../mutations/uploadMultipartMotionAmplificationVideo";
import type { MotionAmplificationSubmission } from "../../mutations/useUploadMotionAmplificationVideoMutation";
import {
  useMotionAmplificationAssignmentCustomersQuery,
  useMotionAmplificationAssignmentFacilitiesQuery,
  useMotionAmplificationAssignmentMachinesQuery,
} from "../../queries/useMotionAmplificationAssignmentQuery";
import { MotionAmplificationParameterForm } from "./MotionAmplificationParameterForm";
import type { MotionAmplificationFormParameters } from "./motionAmplificationParameters";
import {
  ADVANCED_DEFAULTS,
  compatibleMaximumWidth,
  createDefaultParameters,
  DEFAULT_PRESET,
  isMotionAmplificationParametersValid,
  isPresetCompatibleWithFps,
  MAX_INPUT_BYTES,
  MAX_VIDEO_DURATION_SECONDS,
  nyquistFrequencyForFps,
  PRESET_DEFAULTS,
} from "./motionAmplificationParameters";

const FPS_PRESET_TARGETS: Array<{
  fps: number;
  preset: MotionAmplificationPreset;
}> = [
  { fps: 240, preset: "high-frequency-240fps" },
  { fps: 120, preset: "high-frequency-120fps" },
  { fps: 60, preset: "standard-60fps" },
];

function presetForFrameRate(fps: number) {
  if (fps < 60 * 0.995) return "high-stability-review" as const;
  return FPS_PRESET_TARGETS.find(
    (target) => Math.abs(fps - target.fps) <= Math.max(1, target.fps * 0.01),
  )?.preset;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes >= 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadDate(value: string | undefined, fallback: string) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function toSelectOptions(options: { id: string; name: string }[] | undefined) {
  return (options ?? []).map((option) => ({
    value: option.id,
    label: option.name,
  }));
}

export function MotionAmplificationUploadModal({
  open,
  onOpenChange,
  onSubmit,
  rawVideos,
  isLoadingRawVideos,
  resumeUpload,
  onResumeFileSelected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (submission: MotionAmplificationSubmission) => Promise<void>;
  rawVideos: MotionAmplificationRawVideo[];
  isLoadingRawVideos: boolean;
  resumeUpload?: {
    jobName: string;
    fileName: string;
    fileSize: number;
  };
  onResumeFileSelected?: (
    file: File,
    fileHandle?: FileSystemFileHandle,
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [source, setSource] = useState<"upload" | "existing" | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileHandle, setFileHandle] = useState<
    FileSystemFileHandle | undefined
  >();
  const [existingVideo, setExistingVideo] =
    useState<MotionAmplificationRawVideo | null>(null);
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [machineId, setMachineId] = useState<string | null>(null);
  const [preset, setPreset] =
    useState<MotionAmplificationPreset>(DEFAULT_PRESET);
  const [parameters, setParameters] =
    useState<MotionAmplificationFormParameters>(createDefaultParameters());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    fps: localFps,
    durationSeconds: localDurationSeconds,
    width: localWidth,
    height: localHeight,
    isDetecting,
    error: frameRateError,
  } = useVideoFrameRate(file);
  const fps = file ? localFps : (existingVideo?.fps ?? null);
  const durationSeconds = file
    ? localDurationSeconds
    : (existingVideo?.durationSeconds ?? null);
  const dimensions = file
    ? typeof localWidth === "number" && typeof localHeight === "number"
      ? { width: localWidth, height: localHeight }
      : null
    : typeof existingVideo?.width === "number" &&
        typeof existingVideo.height === "number"
      ? { width: existingVideo.width, height: existingVideo.height }
      : null;
  const roundedFps = fps === null ? null : Math.round(fps);
  const isPresetValid = isPresetCompatibleWithFps(preset, fps);
  const areParametersValid = isMotionAmplificationParametersValid(
    parameters,
    fps,
  );
  const customersQuery = useMotionAmplificationAssignmentCustomersQuery();
  const facilitiesQuery =
    useMotionAmplificationAssignmentFacilitiesQuery(customerId);
  const machinesQuery =
    useMotionAmplificationAssignmentMachinesQuery(facilityId);
  const hasPartialAssignment =
    machineId === null && (customerId !== null || facilityId !== null);

  useEffect(() => {
    if (fps === null) return;
    const detectedPreset = presetForFrameRate(fps);
    if (!detectedPreset) return;
    setPreset((current) =>
      current === detectedPreset ? current : detectedPreset,
    );
    setParameters((current) => ({
      ...current,
      ...PRESET_DEFAULTS[detectedPreset],
    }));
  }, [fps]);

  function reset() {
    setSource(null);
    setFile(null);
    setFileHandle(undefined);
    setExistingVideo(null);
    setFileName("");
    setDescription("");
    setCustomerId(null);
    setFacilityId(null);
    setMachineId(null);
    setPreset(DEFAULT_PRESET);
    setParameters(createDefaultParameters());
    setShowAdvanced(false);
    setError(null);
    setIsSubmitting(false);
  }
  function updateParameter<Key extends keyof MotionAmplificationFormParameters>(
    key: Key,
    value: MotionAmplificationFormParameters[Key],
  ) {
    setParameters((current) => ({ ...current, [key]: value }));
  }
  function handlePresetChange(value: string | null) {
    if (!value || !(value in PRESET_DEFAULTS)) return;
    const next = value as MotionAmplificationPreset;
    setPreset(next);
    setParameters((current) => ({ ...current, ...PRESET_DEFAULTS[next] }));
  }
  function handleFileSelected(
    next: File | null,
    handle?: FileSystemFileHandle,
  ) {
    setSource(next ? "upload" : null);
    setFile(next);
    setFileHandle(handle);
    setExistingVideo(null);
    setFileName(next?.name.replace(/\.[A-Za-z0-9]+$/, "") ?? "");
    setError(null);
  }
  async function handleSelectedFile(
    next: File | null,
    handle?: FileSystemFileHandle,
  ) {
    handleFileSelected(next, handle);
    if (!resumeUpload || !next || !onResumeFileSelected) return;

    try {
      setIsSubmitting(true);
      await onResumeFileSelected(next, handle);
      // Not onOpenChange: its stale resume state would re-offer the paused-upload toast.
      reset();
    } catch (selectionError) {
      if (selectionError instanceof MultipartUploadPausedError) {
        reset();
        onOpenChange(false);
        return;
      }
      handleFileSelected(null);
      setError(
        selectionError instanceof Error
          ? selectionError.message
          : t("motionAmplification.resumeFileMismatch"),
      );
    } finally {
      setIsSubmitting(false);
    }
  }
  function handleExistingVideoSelected(rawObjectKey: string | null) {
    const next =
      rawVideos.find((video) => video.rawObjectKey === rawObjectKey) ?? null;
    setSource(next ? "existing" : null);
    setExistingVideo(next);
    setFile(null);
    setFileName(next?.fileName?.replace(/\.[A-Za-z0-9]+$/, "") ?? "");
    setError(null);
  }
  function clearSource() {
    setSource(null);
    setFile(null);
    setExistingVideo(null);
    setFileName("");
    setError(null);
  }
  async function submit() {
    if (!source || (!file && !existingVideo) || !fileName.trim()) return;
    if (hasPartialAssignment) {
      setError(t("motionAmplification.machineAssignmentRequired"));
      return;
    }
    const submittedMaxWidth =
      dimensions && parameters.maxWidth !== null
        ? compatibleMaximumWidth(dimensions, parameters.maxWidth)
        : null;
    const parametersForSubmission =
      submittedMaxWidth === null || submittedMaxWidth === parameters.maxWidth
        ? parameters
        : { ...parameters, maxWidth: submittedMaxWidth };
    if (fps === null) {
      setError(t("motionAmplification.waitForFps"));
      return;
    }
    if (
      durationSeconds !== null &&
      durationSeconds > MAX_VIDEO_DURATION_SECONDS
    ) {
      setError(
        t("motionAmplification.durationError", {
          limit: MAX_VIDEO_DURATION_SECONDS,
        }),
      );
      return;
    }
    if (file && file.size > MAX_INPUT_BYTES) {
      setError(
        t("motionAmplification.fileSizeError", {
          limit: MAX_INPUT_BYTES / (1024 * 1024 * 1024),
        }),
      );
      return;
    }
    if (!isPresetCompatibleWithFps(preset, fps)) {
      setError(t("motionAmplification.presetFpsError"));
      return;
    }
    if (!isMotionAmplificationParametersValid(parametersForSubmission, fps)) {
      if (
        fps !== null &&
        parametersForSubmission.frequencyHighHz !== null &&
        parametersForSubmission.frequencyHighHz > nyquistFrequencyForFps(fps)
      ) {
        setError(
          t("motionAmplification.frequencyError", {
            frequency: nyquistFrequencyForFps(fps).toFixed(0),
          }),
        );
        return;
      }
      setError(t("motionAmplification.numericFieldsError"));
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const details = {
        name: fileName,
        description,
        preset,
        parameters: isMotionAmplificationParametersValid(
          parametersForSubmission,
          fps,
        )
          ? parametersForSubmission
          : { ...PRESET_DEFAULTS[preset], ...ADVANCED_DEFAULTS },
        fps: roundedFps,
        width: dimensions?.width ?? null,
        height: dimensions?.height ?? null,
        machineId,
      };
      if (file) {
        await onSubmit({ ...details, file, fileHandle, existingVideo: null });
      } else if (existingVideo) {
        await onSubmit({ ...details, file: null, existingVideo });
      } else {
        return;
      }
      reset();
      onOpenChange(false);
    } catch (submitError) {
      if (submitError instanceof MultipartUploadPausedError) {
        reset();
        onOpenChange(false);
        return;
      }
      // Mutation errors are surfaced through the global toast handler.
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        // Closing a resume before it starts would offer the paused-upload toast for an upload already underway.
        if (isSubmitting && resumeUpload) return;
        // A pending submission resets the form itself once its upload hands off.
        if (!nextOpen && !isSubmitting) reset();
        onOpenChange(nextOpen);
      }}
      title={t("motionAmplification.uploadVideo")}
      size="lg"
      footer={
        <Group justify="end">
          <Button
            variant="ghost"
            disabled={isSubmitting}
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            {t("motionAmplification.cancel")}
          </Button>
          {!resumeUpload && (
            <Button
              onClick={() => void submit()}
              disabled={
                isSubmitting ||
                !source ||
                (!file && !existingVideo) ||
                !fileName.trim() ||
                (source === "upload" && isDetecting) ||
                (source !== null && fps === null) ||
                (durationSeconds !== null &&
                  durationSeconds > MAX_VIDEO_DURATION_SECONDS) ||
                (file !== null && file.size > MAX_INPUT_BYTES) ||
                !isPresetValid ||
                !areParametersValid ||
                hasPartialAssignment
              }
              loading={isSubmitting}
              loadingText={t("motionAmplification.uploading")}
            >
              {t("motionAmplification.uploadAndProcess")}
            </Button>
          )}
        </Group>
      }
    >
      <Stack gap={20}>
        {resumeUpload && (
          <Card padding="compact">
            <Stack gap={4}>
              <Text>{resumeUpload.jobName}</Text>
              <Text tone="subtle">
                {resumeUpload.fileName} ·{" "}
                {formatFileSize(resumeUpload.fileSize)}
              </Text>
              <Text tone="subtle">
                {t("motionAmplification.resumeGuidance")}
              </Text>
            </Stack>
          </Card>
        )}
        {source === null ? (
          <>
            <div className="relative flex w-full flex-col items-center justify-center gap-(--spacing-24) rounded-(--radius-sm) bg-surface-container-lowest px-(--spacing-16) py-(--spacing-24)">
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 size-full"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 480 188"
              >
                <rect
                  className="stroke-outline-subtle"
                  x="0.5"
                  y="0.5"
                  width="479"
                  height="187"
                  rx="4"
                  strokeDasharray="8 6"
                  strokeLinecap="butt"
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              <span className="text-text-subtext">
                <Icon name="upload" size="lg" tone="inherit" aria-hidden />
              </span>
              <Text tone="subtle">
                {t("motionAmplification.uploadGuidance")}
              </Text>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                aria-label={t("motionAmplification.selectVideo")}
                className="sr-only"
                data-testid="motion-amplification-upload"
                disabled={isSubmitting}
                onChange={(event) => {
                  const selectedFile = event.currentTarget.files?.[0] ?? null;
                  event.currentTarget.value = "";
                  void handleSelectedFile(selectedFile);
                }}
              />
              <Button
                variant="outline"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingText={t("motionAmplification.uploading")}
                onClick={() => {
                  const pickerWindow = window as unknown as Window & {
                    showOpenFilePicker?: (options: {
                      types: Array<{
                        description: string;
                        accept: Record<string, string[]>;
                      }>;
                      multiple: boolean;
                    }) => Promise<FileSystemFileHandle[]>;
                  };
                  if (!pickerWindow.showOpenFilePicker) {
                    fileInputRef.current?.click();
                    return;
                  }

                  void pickerWindow
                    .showOpenFilePicker({
                      types: [
                        {
                          description: t("motionAmplification.videoFiles"),
                          accept: {
                            "video/*": [".mp4", ".mov", ".avi", ".mkv"],
                          },
                        },
                      ],
                      multiple: false,
                    })
                    .then(async ([handle]) => {
                      if (handle)
                        await handleSelectedFile(
                          await handle.getFile(),
                          handle,
                        );
                    })
                    .catch((pickerError: unknown) => {
                      if (
                        pickerError instanceof DOMException &&
                        pickerError.name === "AbortError"
                      )
                        return;
                      setError(t("motionAmplification.fileHandleUnavailable"));
                      fileInputRef.current?.click();
                    });
                }}
              >
                {t("motionAmplification.selectVideo")}
              </Button>
            </div>
            {!resumeUpload && (
              <>
                <Divider centeredText={t("motionAmplification.or")} />
                <Select
                  label={t("motionAmplification.selectExistingVideo")}
                  searchable
                  placeholder={
                    isLoadingRawVideos
                      ? t("motionAmplification.loadingExistingVideos")
                      : t("motionAmplification.selectExistingVideo")
                  }
                  options={rawVideos.map((video) => ({
                    value: video.rawObjectKey,
                    label: `${video.fileName ?? ""} · ${formatUploadDate(video.dateCreated ?? undefined, t("motionAmplification.noUploadTime"))}`,
                  }))}
                  value={null}
                  onChange={handleExistingVideoSelected}
                  disabled={isLoadingRawVideos || rawVideos.length === 0}
                />
              </>
            )}
          </>
        ) : null}
        {source === "upload" && file ? (
          <Card padding="compact">
            <div className="flex w-full items-center gap-(--spacing-12)">
              <Tile tone="muted" padding="compact" radius="sm">
                <Icon name="document" size="md" />
              </Tile>
              <div className="min-w-0 flex-1">
                <Text truncate title={file.name}>
                  {file.name}
                </Text>
                <Text tone="subtle">{formatFileSize(file.size)}</Text>
                <Text tone="subtle">
                  {fps === null
                    ? isDetecting
                      ? t("motionAmplification.detectingFps")
                      : t("motionAmplification.fpsUnavailable")
                    : `${roundedFps} FPS`}
                </Text>
                {frameRateError && (
                  <Text tone="negative">{frameRateError}</Text>
                )}
                {durationSeconds !== null &&
                  durationSeconds > MAX_VIDEO_DURATION_SECONDS && (
                    <Text tone="negative">
                      {t("motionAmplification.videoTooLong", {
                        seconds: durationSeconds.toFixed(2),
                        limit: MAX_VIDEO_DURATION_SECONDS,
                      })}
                    </Text>
                  )}
                {file.size > MAX_INPUT_BYTES && (
                  <Text tone="negative">
                    {t("motionAmplification.fileSizeError", {
                      limit: MAX_INPUT_BYTES / (1024 * 1024 * 1024),
                    })}
                  </Text>
                )}
              </div>
              <IconButton
                variant="ghost"
                intent="destructive"
                icon="trash-delete-bin"
                aria-label={t("motionAmplification.removeAttachedVideo")}
                onClick={() => handleFileSelected(null)}
                disabled={isSubmitting}
              />
            </div>
          </Card>
        ) : source === "existing" && existingVideo ? (
          <Card padding="compact">
            <div className="flex w-full items-center gap-(--spacing-12)">
              <Tile tone="muted" padding="compact" radius="sm">
                <Icon name="document" size="md" />
              </Tile>
              <div className="min-w-0 flex-1">
                <Text truncate title={existingVideo.fileName ?? undefined}>
                  {existingVideo.fileName}
                </Text>
                <Text tone="subtle">
                  {formatFileSize(existingVideo.fileSize)}
                </Text>
                <Text tone="subtle">
                  {fps === null
                    ? t("motionAmplification.fpsUnavailable")
                    : `${roundedFps} FPS`}
                </Text>
                {frameRateError && (
                  <Text tone="negative">{frameRateError}</Text>
                )}
                {durationSeconds !== null &&
                  durationSeconds > MAX_VIDEO_DURATION_SECONDS && (
                    <Text tone="negative">
                      {t("motionAmplification.videoTooLong", {
                        seconds: durationSeconds.toFixed(2),
                        limit: MAX_VIDEO_DURATION_SECONDS,
                      })}
                    </Text>
                  )}
              </div>
              <IconButton
                variant="ghost"
                intent="destructive"
                icon="trash-delete-bin"
                aria-label={t("motionAmplification.removeSelectedVideo")}
                onClick={clearSource}
                disabled={isSubmitting}
              />
            </div>
          </Card>
        ) : null}
        {!resumeUpload && (
          <>
            <Input
              label={t("motionAmplification.fileName")}
              placeholder={t("motionAmplification.fileNamePlaceholder")}
              value={fileName}
              onChange={(event) => setFileName(event.target.value)}
              maxLength={100}
              tooltip={t("motionAmplification.fileNameHint")}
              title={t("motionAmplification.fileNameHint")}
              reserveSubtextSpace={false}
            />
            <Textarea
              label={t("motionAmplification.description")}
              placeholder={t("motionAmplification.descriptionPlaceholder")}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={10000}
            />
            <Group>
              <Select
                label={t("motionAmplification.customer")}
                searchable
                placeholder={t("motionAmplification.selectCustomer")}
                options={toSelectOptions(customersQuery.data)}
                value={customerId}
                onChange={(nextCustomerId) => {
                  setCustomerId(nextCustomerId);
                  setFacilityId(null);
                  setMachineId(null);
                }}
                disabled={isSubmitting || customersQuery.isLoading}
                reserveSubtextSpace={false}
              />
              <Select
                label={t("motionAmplification.facility")}
                searchable
                placeholder={t("motionAmplification.selectFacility")}
                options={toSelectOptions(facilitiesQuery.data)}
                value={facilityId}
                onChange={(nextFacilityId) => {
                  setFacilityId(nextFacilityId);
                  setMachineId(null);
                }}
                disabled={
                  isSubmitting ||
                  customerId === null ||
                  facilitiesQuery.isLoading
                }
                reserveSubtextSpace={false}
              />
            </Group>
            <Select
              label={t("motionAmplification.machine")}
              searchable
              placeholder={t("motionAmplification.selectMachine")}
              options={toSelectOptions(machinesQuery.data)}
              value={machineId}
              onChange={(nextMachineId) => {
                setMachineId(nextMachineId);
              }}
              disabled={
                isSubmitting || facilityId === null || machinesQuery.isLoading
              }
              description={t("motionAmplification.machineAssignmentHint")}
              error={
                hasPartialAssignment
                  ? t("motionAmplification.machineAssignmentRequired")
                  : undefined
              }
              reserveSubtextSpace={false}
            />
            <MotionAmplificationParameterForm
              parameters={parameters}
              updateParameter={updateParameter}
              preset={preset}
              onPresetChange={handlePresetChange}
              fps={fps}
              showAdvanced={showAdvanced}
              onToggleAdvanced={() => setShowAdvanced((current) => !current)}
            />
          </>
        )}
        {error && <Text tone="negative">{error}</Text>}
      </Stack>
    </Modal>
  );
}
